import React, { useState, useRef } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Upload, Pencil, Loader2, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BusinessSheetHeaderProps {
  business: {
    id: string;
    name: string;
    company_banner_url?: string | null;
    company_logo?: string | null;
    category_id: string;
  };
  categoryName: string;
  editMode?: boolean;
  onBannerUpload?: (file: File) => void;
  onLogoUpload?: (file: File) => void;
  onBannerRemove?: () => void;
  onLogoRemove?: () => void;
  uploading?: {
    banner: boolean;
    logo: boolean;
  };
}

const BusinessSheetHeader: React.FC<BusinessSheetHeaderProps> = ({ 
  business, 
  categoryName,
  editMode = false,
  onBannerUpload,
  onLogoUpload,
  onBannerRemove,
  onLogoRemove,
  uploading = { banner: false, logo: false }
}) => {
  const [hoveredElement, setHoveredElement] = useState<'banner' | 'logo' | null>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const handleBannerClick = () => {
    if (editMode && bannerInputRef.current) {
      bannerInputRef.current.click();
    }
  };

  const handleLogoClick = () => {
    if (editMode && logoInputRef.current) {
      logoInputRef.current.click();
    }
  };

  const handleBannerFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onBannerUpload) {
      onBannerUpload(file);
      e.target.value = '';
    }
  };

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onLogoUpload) {
      onLogoUpload(file);
      e.target.value = '';
    }
  };

  return (
    <div className="relative border rounded-lg shadow-sm overflow-hidden bg-white">
      {/* Banner Image */}
      <div 
        className={cn(
          "relative h-32 bg-gradient-to-r from-gray-50 to-gray-100 overflow-hidden",
          editMode && "cursor-pointer group"
        )}
        onClick={handleBannerClick}
        onMouseEnter={() => editMode && setHoveredElement('banner')}
        onMouseLeave={() => setHoveredElement(null)}
        onBlur={() => setHoveredElement(null)}
      >
        {business.company_banner_url ? (
          <img
            src={business.company_banner_url}
            alt={`${business.name} banner`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <span className="text-sm">No banner image</span>
          </div>
        )}

        {/* Edit Banner Overlay */}
        {editMode && (hoveredElement === 'banner' || uploading.banner) && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 transition-opacity">
            {uploading.banner ? (
              <div className="flex items-center space-x-2 text-white">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Uploading banner...</span>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" className="bg-white/90 backdrop-blur-sm">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Banner
                </Button>
                {business.company_banner_url && onBannerRemove && (
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    className="bg-white/90 backdrop-blur-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onBannerRemove();
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remove
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Business Info Section */}
      <div className="relative bg-white px-6 pt-12 pb-4">
        {/* Business Logo */}
        <div 
          className={cn(
            "absolute -top-10 left-6 w-20 h-20 bg-white rounded-full border-4 border-white shadow-lg overflow-hidden",
            editMode && "cursor-pointer group"
          )}
          onClick={handleLogoClick}
          onMouseEnter={() => editMode && setHoveredElement('logo')}
          onMouseLeave={() => setHoveredElement(null)}
          onBlur={() => setHoveredElement(null)}
        >
          {business.company_logo ? (
            <img
              src={business.company_logo}
              alt={`${business.name} logo`}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
              No logo
            </div>
          )}

          {/* Edit Logo Overlay */}
          {editMode && (hoveredElement === 'logo' || uploading.logo) && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 transition-opacity rounded-full">
              {uploading.logo ? (
                <Loader2 className="h-4 w-4 animate-spin text-white" />
              ) : (
                <div className="flex items-center justify-center">
                  {business.company_logo && onLogoRemove ? (
                    <button
                      className="p-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        onLogoRemove();
                      }}
                      title="Remove logo"
                    >
                      <Trash2 className="h-3 w-3 text-white" />
                    </button>
                  ) : (
                    <Pencil className="h-4 w-4 text-white" />
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Business Name and Category */}
        <div className="ml-0">
          <h3 className="text-lg font-semibold">{business.name}</h3>
          <Badge variant="outline" className="mt-1">
            {categoryName}
          </Badge>
        </div>
      </div>

      {/* Hidden File Inputs */}
      {editMode && (
        <>
          <input
            ref={bannerInputRef}
            type="file"
            accept="image/*"
            onChange={handleBannerFileChange}
            className="hidden"
          />
          <input
            ref={logoInputRef}
            type="file"
            accept="image/*"
            onChange={handleLogoFileChange}
            className="hidden"
          />
        </>
      )}
    </div>
  );
};

export default BusinessSheetHeader;